import React from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useTenant } from '../../lib/tenant-context';
import { useLoyaltyDashboard, useLoyaltyToggle } from '../../lib/loyalty-hooks';
import { Card } from '../../components/ui/Card';
import { Users, Star, Gift, Trophy, Power, Loader2 } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
export function LoyaltyDashboardPage() {
  const {
    restaurant,
    role,
    signOut
  } = useTenant();
  const {
    customers,
    loading
  } = useLoyaltyDashboard(restaurant.restaurant_id);
  const {
    isActive,
    loading: toggleLoading,
    saving,
    toggle
  } = useLoyaltyToggle(restaurant.restaurant_id);
  const totalCustomers = customers.length;
  const totalPoints = customers.reduce((sum, c) => sum + (c.current_points || 0), 0);
  const totalRewardsAvailable = customers.reduce((sum, c) => sum + (c.available_rewards_count || 0), 0);
  const totalRewardsRedeemed = customers.reduce((sum, c) => sum + (c.redeemed_rewards_count || 0), 0);
  return <AdminLayout restaurant={restaurant} role={role} onSignOut={signOut} data-id="element-4960">
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-id="element-4961">
        <div data-id="element-4962">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-4963">
            Programa de Lealtad
          </h1>
          <p className="text-gray-500" data-id="element-4964">
            Gestiona tus clientes frecuentes y beneficios
          </p>
        </div>

        {/* Activation toggle */}
        <Card className={`p-6 border-2 transition-colors ${isActive ? 'border-emerald-200 bg-emerald-50/40' : 'border-gray-200 bg-gray-50'}`} data-id="element-4965">
          <div className="flex items-center justify-between gap-6 flex-wrap" data-id="element-4966">
            <div className="flex items-center gap-4" data-id="element-4967">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${isActive ? 'bg-emerald-100' : 'bg-gray-200'}`} data-id="element-4968">
                <Power className={`w-6 h-6 ${isActive ? 'text-emerald-600' : 'text-gray-500'}`} data-id="element-4969" />
              </div>
              <div data-id="element-4970">
                <div className="flex items-center gap-2" data-id="element-4971">
                  <h2 className="font-bold text-gray-900" data-id="element-4972">
                    Sistema de Lealtad
                  </h2>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'}`} data-id="element-4973">
                    {toggleLoading ? '...' : isActive ? 'ACTIVO' : 'INACTIVO'}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-0.5" data-id="element-4974">
                  {isActive ? 'Cada pedido pagado con teléfono acumula puntos para el cliente.' : 'Los pedidos no acumulan puntos. Activa el programa para empezar a premiar a tus clientes.'}
                </p>
              </div>
            </div>

            <button onClick={() => toggle(!isActive)} disabled={saving || toggleLoading} className={`relative inline-flex items-center h-9 px-1 rounded-full transition-colors w-20 shrink-0 disabled:opacity-60 ${isActive ? 'bg-emerald-500' : 'bg-gray-300'}`} aria-label={isActive ? 'Desactivar lealtad' : 'Activar lealtad'} data-id="element-4975">
              <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full bg-white shadow transform transition-transform ${isActive ? 'translate-x-11' : 'translate-x-0'}`} data-id="element-4976">
                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-500" data-id="element-4977" />}
              </span>
            </button>
          </div>
        </Card>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-4978">
          <Card className="p-6" data-id="element-4979">
            <div className="flex items-center gap-4" data-id="element-4980">
              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center shrink-0" data-id="element-4981">
                <Users className="w-6 h-6 text-blue-600" data-id="element-4982" />
              </div>
              <div data-id="element-4983">
                <p className="text-sm text-gray-500 font-medium" data-id="element-4984">
                  Clientes Leales
                </p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-4985">
                  {totalCustomers}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-6" data-id="element-4986">
            <div className="flex items-center gap-4" data-id="element-4987">
              <div className="w-12 h-12 bg-yellow-50 rounded-xl flex items-center justify-center shrink-0" data-id="element-4988">
                <Star className="w-6 h-6 text-yellow-600" data-id="element-4989" />
              </div>
              <div data-id="element-4990">
                <p className="text-sm text-gray-500 font-medium" data-id="element-4991">
                  Puntos Emitidos
                </p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-4992">
                  {totalPoints}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-6" data-id="element-4993">
            <div className="flex items-center gap-4" data-id="element-4994">
              <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center shrink-0" data-id="element-4995">
                <Gift className="w-6 h-6 text-green-600" data-id="element-4996" />
              </div>
              <div data-id="element-4997">
                <p className="text-sm text-gray-500 font-medium" data-id="element-4998">
                  Beneficios Disponibles
                </p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-4999">
                  {totalRewardsAvailable}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-6" data-id="element-5000">
            <div className="flex items-center gap-4" data-id="element-5001">
              <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center shrink-0" data-id="element-5002">
                <Trophy className="w-6 h-6 text-purple-600" data-id="element-5003" />
              </div>
              <div data-id="element-5004">
                <p className="text-sm text-gray-500 font-medium" data-id="element-5005">
                  Beneficios Redimidos
                </p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-5006">
                  {totalRewardsRedeemed}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Customer Table */}
        <Card className="overflow-hidden" data-id="element-5007">
          <div className="p-6 border-b border-gray-200" data-id="element-5008">
            <h2 className="font-bold text-gray-900" data-id="element-5009">Directorio de Clientes</h2>
          </div>
          <div className="overflow-x-auto" data-id="element-5010">
            <table className="w-full text-left text-sm" data-id="element-5011">
              <thead className="bg-gray-50 text-gray-500" data-id="element-5012">
                <tr data-id="element-5013">
                  <th className="px-6 py-4 font-medium" data-id="element-5014">Cliente</th>
                  <th className="px-6 py-4 font-medium" data-id="element-5015">Teléfono</th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-5016">Visitas</th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-5017">
                    Total Gastado
                  </th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-5018">
                    Ticket Promedio
                  </th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-5019">Puntos</th>
                  <th className="px-6 py-4 font-medium" data-id="element-5020">Nivel</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200" data-id="element-5021">
                {loading ? <tr data-id="element-5022">
                    <td colSpan={7} className="px-6 py-8 text-center text-gray-500" data-id="element-5023">
                      Cargando clientes...
                    </td>
                  </tr> : customers.length === 0 ? <tr data-id="element-5024">
                    <td colSpan={7} className="px-6 py-8 text-center text-gray-500" data-id="element-5025">
                      No hay clientes registrados aún.
                    </td>
                  </tr> : customers.map(customer => <tr key={customer.customer_id} className="hover:bg-gray-50" data-id="element-5026">
                      <td className="px-6 py-4 font-medium text-gray-900" data-id="element-5027">
                        {customer.name || 'Sin nombre'}
                      </td>
                      <td className="px-6 py-4 text-gray-500" data-id="element-5028">
                        {customer.phone}
                      </td>
                      <td className="px-6 py-4 text-right text-gray-900" data-id="element-5029">
                        {customer.total_orders}
                      </td>
                      <td className="px-6 py-4 text-right text-gray-900" data-id="element-5030">
                        {formatCurrency(customer.total_spent, restaurant.currency_symbol)}
                      </td>
                      <td className="px-6 py-4 text-right text-gray-500" data-id="element-5031">
                        {formatCurrency(customer.total_spent / Math.max(1, customer.total_orders), restaurant.currency_symbol)}
                      </td>
                      <td className="px-6 py-4 text-right font-bold text-brand-charcoal" data-id="element-5032">
                        {customer.current_points}
                      </td>
                      <td className="px-6 py-4" data-id="element-5033">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-5034">
                          {customer.current_level || 'Inicial'}
                        </span>
                      </td>
                    </tr>)}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </AdminLayout>;
}